/**
 * Copyright © 2025 Delicious AI, LLC
 *
 */

export const Colors = {
  BLACK: '#000000',

  // ------------------------
  GRAY_50: '#FAFAFA',
  GRAY_100: '#F5F5F5',
  GRAY_200: '#EEEEEE',
  GRAY_300: '#E0E0E0',
  GRAY_400: '#BDBDBD',
  GRAY_500: '#9E9E9E',
  GRAY_600: '#757575',
  GRAY_700: '#616161',
  GRAY_800: '#424242',
  GRAY_900: '#212121',
  DARK_GRAY: '#2C2C2C',
  WHITE: '#FFFFFF',
  PRIMARY: '#E31D25',
  SECONDARY: '#1D1F20',
  RED: '#E62937',
  ORANGE: '#F2994A',
  YELLOW: '#FFCB00',
  GREEN_1: '#009E2F',
  GREEN_2: '#27AE60',
  GREEN_3: '#6FCF97',
  BLUE_1: '#0079F1',
  BLUE_2: '#2D9CDB',
  BLUE_3: '#56CCF2',
  BLUE_4: '#0079F1',
  BLUE_5: '#0288d1',
  PURPLE_1: '#873CBE',
  PURPLE_2: '#BB6BD9',

  // Compliance Colors
  PLANGORAM: 'rgba(0,128,255, 0.5)',
  REALOGRAM: 'rgba(249,168,37, 0.5)',
  CORRECTOGRAM: 'rgba(56, 142, 60, 0.5)',
  OOS: '#D32F2F',
  PRICING: 'orange',
  EXTRA: '#F9A825',
  CORRECT: '#388E3C',
  UNIDENTIFIED: '#616161',
}

export const Tints = {
  red: [
    '#eb5757',
    '#d44e4e',
    '#bc4646',
    '#a53d3d',
    '#8d3434',
    '#762c2c',
    '#5e2323',
    '#461a1a',
    '#2f1111',
    '#170909',
    '#000000',
  ],
  orange: [
    '#f2994a',
    '#da8a43',
    '#c27a3b',
    '#a96b34',
    '#915c2c',
    '#794d25',
    '#613d1e',
    '#492e16',
    '#301f0f',
    '#180f07',
    '#000000',
  ],
  yellow: [
    '#f2c94c',
    '#dab544',
    '#c2a13d',
    '#a98d35',
    '#91792e',
    '#796526',
    '#61501e',
    '#493c17',
    '#30280f',
    '#181408',
    '#000000',
  ],
  green: [
    '#219653',
    '#1e874b',
    '#1a7842',
    '#17693a',
    '#145a32',
    '#114b2a',
    '#0d3c21',
    '#0a2d19',
    '#071e11',
    '#030f08',
    '#000000',
  ],
  blue: [
    '#2f80ed',
    '#2a73d5',
    '#2666be',
    '#215aa6',
    '#1c4d8e',
    '#184077',
    '#13335f',
    '#0e2647',
    '#091a2f',
    '#050d18',
    '#000000',
  ],
  purple: [
    '#9b51e0',
    '#8c49ca',
    '#7c41b3',
    '#6d399d',
    '#5d3186',
    '#4e2970',
    '#3e205a',
    '#2e1843',
    '#1f102d',
    '#0f0816',
    '#000000',
  ],
  black: [
    '#000000',
    '#000000',
    '#000000',
    '#000000',
    '#000000',
    '#000000',
    '#000000',
    '#000000',
    '#000000',
    '#000000',
    '#000000',
  ],
  white: [
    '#ffffff',
    '#e6e6e6',
    '#cccccc',
    '#b3b3b3',
    '#999999',
    '#808080',
    '#666666',
    '#4c4c4c',
    '#333333',
    '#191919',
    '#000000',
  ],
}

export const Shades = {
  red: [
    '#eb5757',
    '#ed6868',
    '#ef7979',
    '#f18989',
    '#f39a9a',
    '#f5abab',
    '#f7bcbc',
    '#f9cdcd',
    '#fbdddd',
    '#fdeeee',
    '#ffffff',
  ],
  orange: [
    '#f2994a',
    '#f3a35c',
    '#f5ad6e',
    '#f6b880',
    '#f7c292',
    '#f9cca5',
    '#fad6b7',
    '#fbe0c9',
    '#fcebdb',
    '#fef5ed',
    '#ffffff',
  ],
  yellow: [
    '#f2c94c',
    '#f3ce5e',
    '#f5d470',
    '#f6d982',
    '#f7df94',
    '#f9e4a6',
    '#fae9b7',
    '#fbefc9',
    '#fcf4db',
    '#fefaed',
    '#ffffff',
  ],
  green: [
    '#219653',
    '#37a164',
    '#4dab75',
    '#64b687',
    '#7ac098',
    '#90cba9',
    '#a6d5ba',
    '#bce0cb',
    '#d3eadd',
    '#e9f5ee',
    '#ffffff',
  ],
  blue: [
    '#2f80ed',
    '#448def',
    '#5999f1',
    '#6da6f2',
    '#82b3f4',
    '#97c0f6',
    '#acccf8',
    '#c1d9fa',
    '#d5e6fb',
    '#eaf2fd',
    '#ffffff',
  ],
  purple: [
    '#9b51e0',
    '#a562e3',
    '#af74e6',
    '#b985e9',
    '#c397ec',
    '#cda8f0',
    '#d7b9f3',
    '#e1cbf6',
    '#ebdcf9',
    '#f5eefc',
    '#ffffff',
  ],
  black: [
    '#000000',
    '#1a1a1a',
    '#333333',
    '#4d4d4d',
    '#666666',
    '#808080',
    '#999999',
    '#b3b3b3',
    '#cccccc',
    '#e6e6e6',
    '#ffffff',
  ],
  white: [
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#ffffff',
    '#ffffff',
  ],
}

export const SortedShadesList = [
  Colors.RED,
  Colors.ORANGE,
  Colors.YELLOW,
  Colors.GREEN_1,
  Colors.BLUE_1,
  Colors.PURPLE_1,
  Shades.red[1],
  Shades.orange[1],
  Shades.yellow[1],
  Shades.green[1],
  Shades.blue[1],
  Shades.purple[1],
  Tints.red[1],
  Tints.orange[1],
  Tints.yellow[1],
  Tints.green[1],
  Tints.blue[1],
  Tints.purple[1],
  Shades.red[2],
  Shades.orange[2],
  Shades.yellow[2],
  Shades.green[2],
  Shades.blue[2],
  Shades.purple[2],
  Tints.red[2],
  Tints.orange[2],
  Tints.yellow[2],
  Tints.green[2],
  Tints.blue[2],
  Tints.purple[2],
  Shades.red[3],
  Shades.orange[3],
  Shades.yellow[3],
  Shades.green[3],
  Shades.blue[3],
  Shades.purple[3],
  Tints.red[3],
  Tints.orange[3],
  Tints.yellow[3],
  Tints.green[3],
  Tints.blue[3],
  Tints.purple[3],
  Shades.red[4],
  Shades.orange[4],
  Shades.yellow[4],
  Shades.green[4],
  Shades.blue[4],
  Shades.purple[4],
  Tints.red[4],
  Tints.orange[4],
  Tints.yellow[4],
  Tints.green[4],
  Tints.blue[4],
  Tints.purple[4],
  Shades.red[5],
  Shades.orange[5],
  Shades.yellow[5],
  Shades.green[5],
  Shades.blue[5],
  Shades.purple[5],
  Tints.red[5],
  Tints.orange[5],
  Tints.yellow[5],
  Tints.green[5],
  Tints.blue[5],
  Tints.purple[5],
  Shades.red[6],
  Shades.orange[6],
  Shades.yellow[6],
  Shades.green[6],
  Shades.blue[6],
  Shades.purple[6],
  Tints.red[6],
  Tints.orange[6],
  Tints.yellow[6],
  Tints.green[6],
  Tints.blue[6],
  Tints.purple[6],
  Shades.red[7],
  Shades.orange[7],
  Shades.yellow[7],
  Shades.green[7],
  Shades.blue[7],
  Shades.purple[7],
  Tints.red[7],
  Tints.orange[7],
  Tints.yellow[7],
  Tints.green[7],
  Tints.blue[7],
  Tints.purple[7],
  Shades.red[8],
  Shades.orange[8],
  Shades.yellow[8],
  Shades.green[8],
  Shades.blue[8],
  Shades.purple[8],
  Tints.red[8],
  Tints.orange[8],
  Tints.yellow[8],
  Tints.green[8],
  Tints.blue[8],
  Tints.purple[8],
]
